import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { notifyEventParticipants } from './useEventNotifications';

export interface Event {
  id: string;
  title: string;
  description: string | null;
  event_type: 'ride' | 'garage_day' | 'cleanup' | 'meetup' | 'training' | 'overlanding_trip' | 'competition';
  created_by: string;
  route_id: string | null;
  start_date: string;
  end_date: string | null;
  meeting_point_geom: any;
  meeting_point_description: string | null;
  access_level: 'public' | 'private' | 'club_only';
  club_id: string | null;
  max_participants: number | null;
  rsvp_required: boolean;
  waiver_required: boolean;
  waiver_text: string | null;
  status: 'upcoming' | 'active' | 'completed' | 'cancelled';
  difficulty_estimate: 'easy' | 'moderate' | 'hard' | 'extreme' | null;
  estimated_duration_hours: number | null;
  equipment_requirements: string[] | null;
  photo_url: string | null;
  photo_urls: string[] | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateEventData {
  title: string;
  description?: string;
  event_type: Event['event_type'];
  route_id?: string;
  start_date: string;
  end_date?: string;
  meeting_point_description?: string;
  access_level?: Event['access_level'];
  max_participants?: number;
  rsvp_required?: boolean;
  difficulty_estimate?: Event['difficulty_estimate'];
  estimated_duration_hours?: number;
  equipment_requirements?: string[];
  photo_url?: string;
  photo_urls?: string[];
  notes?: string;
  waiver_required?: boolean;
  waiver_text?: string;
}

// Fetch all events (with optional filters)
export const useEvents = (filters?: { status?: Event['status']; eventType?: Event['event_type'] }) => {
  return useQuery({
    queryKey: ['events', filters],
    queryFn: async () => {
      let query = supabase
        .from('events')
        .select('*')
        .order('start_date', { ascending: true });

      if (filters?.status) {
        query = query.eq('status', filters.status);
      }

      if (filters?.eventType) {
        query = query.eq('event_type', filters.eventType);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching events:', error);
        throw error;
      }

      return data as Event[];
    },
  });
};

// Fetch single event detail
export const useEventDetail = (eventId: string | undefined) => {
  return useQuery({
    queryKey: ['event', eventId],
    queryFn: async () => {
      if (!eventId) throw new Error('Event ID is required');

      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('id', eventId)
        .single();

      if (error) {
        console.error('Error fetching event:', error);
        throw error;
      }

      return data as Event;
    },
    enabled: !!eventId,
  });
};

// Fetch user's events (hosting or attending)
export const useUserEvents = (userId: string | undefined) => {
  return useQuery({
    queryKey: ['user-events', userId],
    queryFn: async () => {
      if (!userId) throw new Error('User ID is required');

      // Fetch events user is hosting
      const { data: hostedEvents, error: hostedError } = await supabase
        .from('events')
        .select('*')
        .eq('created_by', userId)
        .order('start_date', { ascending: true });

      if (hostedError) throw hostedError;

      // Fetch events user is attending
      const { data: participantEvents, error: participantError } = await supabase
        .from('event_participants')
        .select('event_id, status')
        .eq('user_id', userId)
        .in('status', ['going', 'maybe']);

      if (participantError) throw participantError;

      const eventIds = participantEvents?.map(p => p.event_id) || [];

      let attendingEvents: Event[] = [];
      if (eventIds.length > 0) {
        const { data, error } = await supabase
          .from('events')
          .select('*')
          .in('id', eventIds)
          .order('start_date', { ascending: true });

        if (error) throw error;
        attendingEvents = data as Event[];
      }

      return {
        hosting: hostedEvents as Event[],
        attending: attendingEvents,
      };
    },
    enabled: !!userId,
  });
};

// Create event mutation
export const useCreateEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (eventData: CreateEventData) => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User must be authenticated');
      }

      const { data, error } = await supabase
        .from('events')
        .insert({
          ...eventData,
          created_by: user.id,
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating event:', error);
        throw error;
      }

      return data as Event;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      toast.success('Event created successfully!');
    },
    onError: (error) => {
      console.error('Failed to create event:', error);
      toast.error('Failed to create event. Please try again.');
    },
  });
};

// Update event mutation
export const useUpdateEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ eventId, updates }: { eventId: string; updates: Partial<CreateEventData> }) => {
      const { data, error } = await supabase
        .from('events')
        .update(updates)
        .eq('id', eventId)
        .select()
        .single();

      if (error) {
        console.error('Error updating event:', error);
        throw error;
      }

      return data as Event;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      queryClient.invalidateQueries({ queryKey: ['event', variables.eventId] });
      toast.success('Event updated successfully!');
    },
    onError: (error) => {
      console.error('Failed to update event:', error);
      toast.error('Failed to update event. Please try again.');
    },
  });
};

// Cancel event mutation
export const useCancelEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (eventId: string) => {
      const { data, error } = await supabase
        .from('events')
        .update({ status: 'cancelled' })
        .eq('id', eventId)
        .select()
        .single();

      if (error) {
        console.error('Error cancelling event:', error);
        throw error;
      }

      return data as Event;
    },
    onSuccess: async (event, eventId) => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      queryClient.invalidateQueries({ queryKey: ['event', eventId] });
      
      // Notify all participants about cancellation
      await notifyEventParticipants(
        eventId,
        'event_cancelled',
        'Event Cancelled',
        `The event "${event.title}" has been cancelled.`
      );
      
      toast.success('Event cancelled successfully');
    },
    onError: (error) => {
      console.error('Failed to cancel event:', error);
      toast.error('Failed to cancel event. Please try again.');
    },
  });
};
